import React, { useMemo, useState } from 'react';
import { Modal, TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ItemsReportOptionItem } from 'shared-models/browser';
import type { ProductView } from '../../../../../services/products/products.service';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from './pos-items-report.module.scss';

export type PosItemsProductPickerModalProps = {
  opened: boolean;
  products: ProductView[];
  optionItems: ItemsReportOptionItem[];
  selectedProductIds: number[];
  selectedOptionItemIds: number[];
  onClose: () => void;
  onConfirm: (productIds: number[], optionItemIds: number[]) => void;
};

type Tile = {
  key: string;
  id: number;
  label: string;
  hint: string | null;
  testId: string;
};

const toggleId = (current: number[], id: number): number[] =>
  current.includes(id) ? current.filter((value) => value !== id) : [...current, id];

const matchesTerm = (term: string, ...fields: Array<string | null>): boolean =>
  !term || fields.some((field) => (field ?? '').toLowerCase().includes(term));

// Summary: Product / size / combo multi-picker for דוח פריטים — one search spans all three
// sections so a term like "צ'ייסר" surfaces the product and the option together.
const PosItemsProductPickerModalComponent: React.FC<PosItemsProductPickerModalProps> = ({
  opened,
  products,
  optionItems,
  selectedProductIds,
  selectedOptionItemIds,
  onClose,
  onConfirm,
}) => {
  const [search, setSearch] = useState('');
  const [productDraft, setProductDraft] = useState<number[]>(selectedProductIds);
  const [optionDraft, setOptionDraft] = useState<number[]>(selectedOptionItemIds);

  // Reopening must start from what the report currently filters on, not the last abandoned draft.
  React.useEffect(() => {
    if (opened) {
      setProductDraft(selectedProductIds);
      setOptionDraft(selectedOptionItemIds);
      setSearch('');
    }
  }, [opened, selectedProductIds, selectedOptionItemIds]);

  const term = search.trim().toLowerCase();

  const productTiles = useMemo<Tile[]>(
    () => products
      .filter((product) => matchesTerm(term, product.name))
      .map((product) => ({
        key: `p-${product.id}`,
        id: product.id,
        label: product.name,
        hint: null,
        testId: `items-report-picker-product-${product.id}`,
      })),
    [products, term],
  );

  const optionTiles = useMemo(() => {
    const visible = optionItems.filter((option) =>
      matchesTerm(term, option.itemName, option.groupName, option.productName));
    const toTile = (option: ItemsReportOptionItem): Tile => ({
      key: `o-${option.itemId}`,
      id: option.itemId,
      label: option.kind === 'variant' && option.productName
        ? `${option.productName} (${option.itemName})`
        : option.itemName,
      hint: option.groupName,
      testId: `items-report-picker-option-${option.itemId}`,
    });
    return {
      variants: visible.filter((option) => option.kind === 'variant').map(toTile),
      combos: visible.filter((option) => option.kind === 'combo').map(toTile),
    };
  }, [optionItems, term]);

  const totalSelected = productDraft.length + optionDraft.length;
  const matchedOptionTiles = [...optionTiles.variants, ...optionTiles.combos];
  const matchCount = productTiles.length + matchedOptionTiles.length;
  const hasAnyMatch = matchCount > 0;
  const allMatchesSelected = hasAnyMatch
    && productTiles.every((tile) => productDraft.includes(tile.id))
    && matchedOptionTiles.every((tile) => optionDraft.includes(tile.id));

  // Select-all acts on the current matches only, so it never silently picks up hidden items.
  const toggleAllMatches = () => {
    const productMatchIds = productTiles.map((tile) => tile.id);
    const optionMatchIds = matchedOptionTiles.map((tile) => tile.id);
    if (allMatchesSelected) {
      setProductDraft((current) => current.filter((id) => !productMatchIds.includes(id)));
      setOptionDraft((current) => current.filter((id) => !optionMatchIds.includes(id)));
      return;
    }
    setProductDraft((current) => Array.from(new Set([...current, ...productMatchIds])));
    setOptionDraft((current) => Array.from(new Set([...current, ...optionMatchIds])));
  };

  const renderSection = (title: string, tiles: Tile[], draft: number[], onToggle: (id: number) => void) => {
    if (tiles.length === 0) {
      return null;
    }
    return (
      <div className={classes.pickerSection}>
        <div className={classes.pickerSectionTitle}>{`${title} (${tiles.length})`}</div>
        <div className={classes.pickerGrid}>
          {tiles.map((tile) => {
            const isSelected = draft.includes(tile.id);
            return (
              <UnstyledButton
                key={tile.key}
                component="button"
                type="button"
                data-testid={tile.testId}
                aria-pressed={isSelected}
                className={isSelected ? `${classes.pickerTile} ${classes.pickerTileSelected}` : classes.pickerTile}
                onClick={() => onToggle(tile.id)}
              >
                {isSelected && (
                  <span className={classes.pickerTileMark}>
                    <FontAwesomeIcon icon={faCheck as IconProp} />
                  </span>
                )}
                <span className={classes.pickerTileName}>{tile.label}</span>
                {tile.hint && <span className={classes.pickerTileGroup}>{tile.hint}</span>}
              </UnstyledButton>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<strong className={classes.pickerTitle}>בחירת פריטים</strong>}
      size="lg"
      centered
      radius="md"
      dir="rtl"
      zIndex={500}
      closeButtonProps={{ 'data-testid': 'items-report-picker-close' } as Record<string, string>}
      classNames={{ root: navyModal.root, body: classes.pickerBody }}
    >
      <TextInput
        data-testid="items-report-picker-search"
        placeholder="חיפוש מוצר, גודל או מרכיב"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
      />

      {term !== '' && hasAnyMatch && (
        <div className={classes.pickerSelectAllRow}>
          <span className={classes.pickerMatchCount}>{`${matchCount} תוצאות`}</span>
          <UnstyledButton
            component="button"
            type="button"
            data-testid="items-report-picker-select-all"
            className={classes.pickerSelectAll}
            onClick={toggleAllMatches}
          >
            {allMatchesSelected ? 'נקה בחירה' : 'בחר הכל'}
          </UnstyledButton>
        </div>
      )}

      <div className={classes.pickerScroll}>
        {hasAnyMatch ? (
          <>
            {renderSection('מוצרים', productTiles, productDraft, (id) =>
              setProductDraft((current) => toggleId(current, id)))}
            {renderSection('גדלים', optionTiles.variants, optionDraft, (id) =>
              setOptionDraft((current) => toggleId(current, id)))}
            {renderSection('מרכיבי דילים', optionTiles.combos, optionDraft, (id) =>
              setOptionDraft((current) => toggleId(current, id)))}
          </>
        ) : (
          <div className={classes.pickerEmpty}>לא נמצאו פריטים</div>
        )}
      </div>

      <div className={classes.pickerFooter}>
        <span className={classes.pickerCount}>{`נבחרו ${totalSelected}`}</span>
        <div className={classes.pickerActions}>
          <Button variant="cancel" onClick={onClose} dataTestId="items-report-picker-cancel">
            ביטול
          </Button>
          <Button
            variant="confirm"
            onClick={() => onConfirm(productDraft, optionDraft)}
            disabled={totalSelected === 0}
            dataTestId="items-report-picker-confirm"
          >
            אישור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const PosItemsProductPickerModal = React.memo(PosItemsProductPickerModalComponent);
